import React from "react";
import { Container, Typography, Box } from "@mui/material";
import { motion } from "framer-motion";
import { colors, mono } from "../theme/tokens";

const features = [
  {
    tag: "PROJECTS",
    title: "Project Organization",
    description:
      "Group related tasks into projects. Simple structure that actually makes sense.",
    benefits: [
      "Clean project overview",
      "Easy task grouping",
      "No complex hierarchies",
    ],
  },
  {
    tag: "TASKS",
    title: "Smart Task Management",
    description:
      "Add tasks quickly, set priorities, and track progress without the bloat.",
    benefits: ["Quick task creation", "Priority levels", "Progress tracking"],
  },
  {
    tag: "NOTES",
    title: "Integrated Notes",
    description:
      "Capture ideas and context right where you work. No app switching required.",
    benefits: ["Contextual notes", "Rich text formatting", "Linked to tasks"],
  },
  {
    tag: "TIME",
    title: "Time Tracking",
    description:
      "Simple time tracking that doesn't get in your way. Know where your time goes.",
    benefits: ["One-click timers", "Automatic tracking"],
  },
];

const Features: React.FC = () => (
  <Box
    id="features"
    sx={{
      background: colors.surface,
      py: { xs: 8, md: 12 },
      px: { xs: 3, md: 6 },
      borderTop: `1px solid ${colors.line}`,
    }}
  >
    <Container maxWidth={false} sx={{ maxWidth: "1120px", px: 0 }}>
      <Box sx={{ textAlign: "center", mb: 8 }}>
        <Typography
          component={motion.h2}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          sx={{
            fontSize: { xs: "30px", md: "40px" },
            fontWeight: 800,
            letterSpacing: "-0.01em",
            color: colors.ink,
            m: 0,
            mb: 2,
          }}
        >
          Everything you need.{" "}
          <Box component="span" sx={{ color: colors.primary }}>
            Nothing you don't.
          </Box>
        </Typography>
        <Typography
          sx={{
            fontSize: "17px",
            color: colors.muted,
            maxWidth: "560px",
            mx: "auto",
          }}
        >
          Four core features that work together seamlessly. No feature creep, no
          overwhelming options.
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
          gap: 3,
        }}
      >
        {features.map((feature, index) => (
          <Box
            key={feature.tag}
            component={motion.div}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5, delay: index * 0.08 }}
            sx={{
              border: `1px solid ${colors.border}`,
              borderRadius: "16px",
              p: 4,
              background: colors.surface,
              transition: "all 0.3s ease",
              "&:hover": {
                borderColor: "#DFDCF9",
                boxShadow: "0 12px 32px rgba(20,20,40,0.06)",
              },
            }}
          >
            <Box
              component="span"
              sx={{
                display: "inline-block",
                fontFamily: mono,
                fontSize: "11px",
                fontWeight: 700,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                color: colors.primary,
                background: colors.primaryTint,
                px: 1.25,
                py: "5px",
                borderRadius: "6px",
                mb: 2.5,
              }}
            >
              {feature.tag}
            </Box>
            <Typography
              component="h3"
              sx={{
                fontSize: "20px",
                fontWeight: 700,
                color: colors.ink,
                m: 0,
                mb: 1.25,
              }}
            >
              {feature.title}
            </Typography>
            <Typography
              sx={{ color: colors.muted, lineHeight: 1.6, mb: 2.5 }}
            >
              {feature.description}
            </Typography>
            {feature.benefits.map((benefit) => (
              <Box
                key={benefit}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: colors.primary,
                    flexShrink: 0,
                  }}
                />
                <Typography sx={{ fontSize: "13px", color: colors.muted }}>
                  {benefit}
                </Typography>
              </Box>
            ))}
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          textAlign: "center",
          mt: 8,
          p: 5,
          borderRadius: "16px",
          background: colors.primarySoft,
          border: `1px solid ${colors.primaryTint}`,
        }}
      >
        <Typography
          component="h3"
          sx={{
            fontSize: "22px",
            fontWeight: 700,
            color: colors.ink,
            m: 0,
            mb: 1.25,
          }}
        >
          Ready to simplify your workflow?
        </Typography>
        <Typography sx={{ color: colors.muted, m: 0 }}>
          Start with our free plan. No credit card required.
        </Typography>
      </Box>
    </Container>
  </Box>
);

export default Features;
